/**
 * All tooltip texts used by InfoTooltip across the app.
 * Single source of truth for (i) icon copy: Arena, Group leaderboard, Friend stats, Create (market pick descriptions).
 */
import { MARKET_IDS } from '../src/create/constants/matchDetails.constants';

// --- Arena stats banner ---
export const ARENA_STATS_TOOLTIPS = {
  WIN_RATE:
    'Percentage of completed 1v1 challenges you won (wins ÷ total). Group challenges are not counted.',
  FAVORITE_BETS:
    'Your top 2–3 prediction types by number of picks in completed challenges.',
  STREAK:
    "Your current 1v1 winning streak: how many 1v1 challenges in a row you've won. Group challenges don't affect this.",
} as const;

// --- Group leaderboard column headers ---
export const GROUP_LEADERBOARD_HEADER_TOOLTIPS = {
  COINS: 'Net coins won or lost across all completed challenges in this group.',
  GAMES: 'Number of challenges played in this group.',
  WINS: 'Number of challenges won in this group.',
} as const;

// --- Friend stats (1v1 history) Coins Balance card ---
export const COINS_BALANCE_TOOLTIP =
  'Your net coin result from all completed challenges against this friend. Positive means you\'ve won more coins than you\'ve lost.';

// --- Friend stats (1v1 history) Head to Head banner ---
export const HEAD_TO_HEAD_TOOLTIP =
  'Your stats vs this friend: wins, total points from completed challenges, and pick accuracy (correct outcomes).';

// --- Challenges: waiting-for-friends Remind button ---
export const CHALLENGE_REMIND_BUTTON_TOOLTIP =
  'Send a reminder to friends who haven’t accepted the challenge yet.';

// --- Create flow: market prediction type descriptions (PickInfoIcon on match details) ---
export const MARKET_BET_DESCRIPTIONS: Partial<Record<number, string>> = {
  [MARKET_IDS.MATCH_WINNER]:
    'Predict the final result: Home team wins, Away team wins, or a Draw. Example: Picking "Home" is correct only if the home team wins the match.',
  [MARKET_IDS.DOUBLE_CHANCE]:
    'Cover two out of three possible results to lower your risk. Example: "Home or Draw" is correct if the home team wins OR if the match ends in a tie.',
  [MARKET_IDS.BTTS]:
    'Will both teams score at least one goal during the match? Example: Picking "Yes" is correct if the final score is 1-1, 2-1, etc. Picking "No" is correct if it ends 1-0 or 0-0.',
  [MARKET_IDS.OVER_UNDER_15]:
    'Will the total goals scored by both teams combined be 2 or more (Over) or 1 or less (Under)? Example: Picking "Over 1.5" is correct if the final score is 1-1, 2-0, etc.',
  [MARKET_IDS.OVER_UNDER_25]:
    'Will the total goals scored by both teams be 3 or more (Over) or 2 or less (Under)? Example: Picking "Under 2.5" is correct if the match ends 0-0, 1-0, or 1-1.',
  [MARKET_IDS.OVER_UNDER_35]:
    'Will the total goals scored by both teams be 4 or more (Over) or 3 or less (Under)? Example: Picking "Over 3.5" is correct if the score is 2-2, 3-1, etc.',
  [MARKET_IDS.EXACT_GOALS]:
    'Predict the exact combined number of goals scored by both teams. Example: Picking "3 goals" is correct if the final score is 2-1 or 3-0.',
  [MARKET_IDS.CORRECT_SCORE]:
    'Predict the exact final score of the match. Example: Picking "2-1" is correct only if the home team wins exactly two to one.',
  [MARKET_IDS.HALF_TIME_CORRECT_SCORE]:
    'Predict the exact score when the referee blows the whistle for half-time. Example: Picking "1-0" is correct if the home team leads by exactly one goal at the break.',
  [MARKET_IDS.CORNERS]:
    'Predict whether the total number of corner kicks taken by both teams will be over or under a specific number. Example: Picking "Over 9.5" is correct if there are 10 or more corners.',
  [MARKET_IDS.CLEAN_SHEET_HOME]:
    'Will the home team prevent the away team from scoring any goals? Example: Picking "Yes" is correct if the away team scores 0 goals.',
  [MARKET_IDS.CLEAN_SHEET_AWAY]:
    'Will the away team prevent the home team from scoring any goals? Example: Picking "Yes" is correct if the home team scores 0 goals.',
  [MARKET_IDS.OVER_UNDER_05_1ST_HALF]:
    'Will there be at least one goal scored before half-time? Example: Picking "Over 0.5" is correct if anyone scores in the first half.',
  [MARKET_IDS.OVER_UNDER_15_1ST_HALF]:
    'Will there be 2 or more goals scored before half-time? Example: Picking "Under 1.5" is correct if the first half ends 0-0 or 1-0.',
  [MARKET_IDS.OVER_UNDER_25_1ST_HALF]:
    'Will there be 3 or more goals scored before half-time? Example: Picking "Over 2.5" is correct if the first half ends 2-1, 3-0, etc.',
  [MARKET_IDS.HANDICAP_RESULT]:
    'European handicap: one team gets a virtual goal advantage or disadvantage. Each line is a separate pick. "Home -1" means Home has a 1-goal disadvantage; "Draw" is correct if Home wins by exactly 1; "Away" is correct if Home draws or loses.',
  [MARKET_IDS.HANDICAP_MINUS_4]:
    'Handicap -4: Home starts with a 4-goal disadvantage. "Home" is correct if Home wins by 5+; "Draw" is correct if Home wins by exactly 4; "Away" is correct if Home wins by 3 or less, draws, or loses.',
  [MARKET_IDS.HANDICAP_MINUS_3]:
    'Handicap -3: Home starts with a 3-goal disadvantage. "Home" is correct if Home wins by 4+; "Draw" is correct if Home wins by exactly 3; "Away" is correct otherwise.',
  [MARKET_IDS.HANDICAP_MINUS_2]:
    'Handicap -2: Home starts with a 2-goal disadvantage. "Home" is correct if Home wins by 3+; "Draw" is correct if Home wins by exactly 2; "Away" is correct otherwise.',
  [MARKET_IDS.HANDICAP_MINUS_1]:
    'Handicap -1: Home starts with a 1-goal disadvantage. "Home" is correct if Home wins by 2+; "Draw" is correct if Home wins by exactly 1 goal; "Away" is correct if Home draws or loses.',
  [MARKET_IDS.HANDICAP_0]:
    'Handicap 0: No adjustment. Same as Match Winner (1X2): "Home", "Draw", or "Away" by actual score.',
  [MARKET_IDS.HANDICAP_PLUS_1]:
    'Handicap +1: Home starts with a 1-goal advantage. "Home" is correct if Home wins or draws; "Draw" is correct if Home loses by exactly 1; "Away" is correct if Home loses by 2+.',
  [MARKET_IDS.HANDICAP_PLUS_2]:
    'Handicap +2: Home starts with a 2-goal advantage. "Home" is correct if Home wins, draws, or loses by 1; "Draw" is correct if Home loses by exactly 2; "Away" is correct if Home loses by 3+.',
  [MARKET_IDS.HANDICAP_PLUS_3]:
    'Handicap +3: Home starts with a 3-goal advantage. "Draw" is correct if Home loses by exactly 3; "Away" is correct if Home loses by 4+.',
  [MARKET_IDS.HANDICAP_PLUS_4]:
    'Handicap +4: Home starts with a 4-goal advantage. "Draw" is correct if Home loses by exactly 4; "Away" is correct if Home loses by 5+.',
  [MARKET_IDS.HALF_TIME_FULL_TIME]:
    'Predict who will be winning at half-time AND who will win the final match. Example: Picking "Draw/Home" is correct if it is tied at half-time, but the home team wins the game.',
  [MARKET_IDS.HIGHEST_SCORING_HALF]:
    'Which half of the match will have more goals scored? Example: Picking "2nd Half" is correct if 1 goal is scored in the first half and 2 goals are scored in the second half.',
};
